import { useEffect, useState, useRef } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { Save, ArrowLeft, Upload, X, Replace as ReplaceIcon, Play, Youtube, Link2 } from "lucide-react";
import { api, fileUrl, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";

const empty = {
  slug: "",
  location: "",
  description: "",
  media: [],
  cover_image: "",
  seo_title: "",
  seo_description: "",
  is_published: true,
  is_pinned: false,
  is_archived: false,
};

function slugify(s) {
  return (s || "").toLowerCase().replace(/[^\w\s-]/g, "").trim().replace(/[-\s]+/g, "-");
}

const isVideoUrl = (url) => /\.(mp4|webm|mov|m4v|ogg|ogv)$/i.test(url || "");
const isImageMedia = (m) => m && (m.type === "image" || (!m.type && !isVideoUrl(m.url)));

export default function AdminProjectForm() {
  const { id } = useParams();
  const isEdit = !!id;
  const [form, setForm] = useState(empty);
  const [submitting, setSubmitting] = useState(false);
  const [uploading, setUploading] = useState(false);
  const navigate = useNavigate();
  const fileRef = useRef();
  const coverRef = useRef();
  const replaceRef = useRef();
  const [replaceIndex, setReplaceIndex] = useState(null);
  const [embedUrl, setEmbedUrl] = useState("");
  const [addingEmbed, setAddingEmbed] = useState(false);

  const addVideoEmbed = async () => {
    if (!embedUrl.trim()) return;
    setAddingEmbed(true);
    try {
      const { data } = await api.post("/media/embed", { url: embedUrl.trim() });
      setForm((s) => {
        const next = [...(s.media || []), data];
        let cover = s.cover_image;
        if (!cover && data.poster_url) cover = data.poster_url;
        return { ...s, media: next, cover_image: cover };
      });
      setEmbedUrl("");
      toast.success("Video added — no upload needed");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not parse that link.");
    } finally {
      setAddingEmbed(false);
    }
  };

  useEffect(() => {
    if (isEdit) {
      api.get(`/projects/by-id/${id}`).then((r) => {
        const found = r.data;
        if (found) setForm({ ...empty, ...found });
      }).catch(() => toast.error("Project not found"));
    }
  }, [id, isEdit]);

  const upload = async (file) => {
    const fd = new FormData();
    fd.append("file", file);
    setUploading(true);
    try {
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      return data;
    } finally { setUploading(false); }
  };

  const onAddMedia = async (e) => {
    const files = Array.from(e.target.files || []);
    for (const f of files) {
      try {
        const r = await upload(f);
        setForm((s) => {
          const newItem = {
            url: r.url,
            type: r.media_type,
            ...(r.poster_url ? { poster_url: r.poster_url } : {}),
            ...(r.id ? { file_id: r.id } : {}),
            ...(r.processing ? { processing: true } : {}),
          };
          const next = [...(s.media || []), newItem];
          let cover = s.cover_image;
          if (!cover) {
            const firstImg = next.find((m) => m.type === "image");
            if (firstImg) cover = firstImg.url;
            else cover = next[0]?.poster_url || ""; // leave blank for video-only — server fills in poster after transcode
          }
          return { ...s, media: next, cover_image: cover };
        });
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error("Upload failed:", err.response?.status, err.response?.data, err.message);
        toast.error(formatApiErrorDetail(err.response?.data?.detail) || `Upload failed: ${err.message || "network error"}`);
      }
    }
    if (fileRef.current) fileRef.current.value = "";
  };

  const onUploadCover = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Cover must be an image");
      return;
    }
    try {
      const r = await upload(file);
      setForm((s) => ({ ...s, cover_image: r.url }));
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const removeMedia = (i) => {
    setForm((s) => {
      const removed = s.media[i];
      const next = s.media.filter((_, idx) => idx !== i);
      let cover = s.cover_image;
      if (removed && removed.url === s.cover_image) {
        const replacement = next.find((m) => m.type === "image") || next[0];
        cover = replacement
          ? (replacement.type === "image" ? replacement.url : (replacement.poster_url || ""))
          : "";
      }
      return { ...s, media: next, cover_image: cover };
    });
  };

  const triggerReplace = (index) => {
    setReplaceIndex(index);
    if (replaceRef.current) { replaceRef.current.value = ""; replaceRef.current.click(); }
  };

  const onReplaceMedia = async (e) => {
    const file = e.target.files?.[0];
    const idx = replaceIndex;
    if (!file || idx === null || idx === undefined) return;
    try {
      const r = await upload(file);
      setForm((s) => {
        const next = [...(s.media || [])];
        const newItem = {
          url: r.url,
          type: r.media_type,
          ...(r.poster_url ? { poster_url: r.poster_url } : {}),
          ...(r.id ? { file_id: r.id } : {}),
          ...(r.processing ? { processing: true } : {}),
        };
        if (next[idx]) next[idx] = newItem;
        const wasCover = s.cover_image && s.media?.[idx]?.url === s.cover_image;
        const newCover = wasCover ? (newItem.type === "image" ? newItem.url : (newItem.poster_url || newItem.url)) : s.cover_image;
        return { ...s, media: next, cover_image: newCover };
      });
      toast.success(`Media replaced (slot ${idx + 1})`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setReplaceIndex(null);
      if (replaceRef.current) replaceRef.current.value = "";
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    const payload = { ...form };
    if (payload.slug) payload.slug = slugify(payload.slug);
    // Only auto-set cover_image to a non-video URL — let backend pick a
    // poster for video-only projects after transcode.
    if (!payload.cover_image && payload.media?.length) {
      const firstImg = payload.media.find((m) => isImageMedia(m));
      if (firstImg) payload.cover_image = firstImg.url;
      else payload.cover_image = payload.media[0]?.poster_url || ""; // blank → backend fills
    }
    try {
      if (isEdit) {
        await api.patch(`/projects/${id}`, payload);
        toast.success("Project updated");
      } else {
        await api.post("/projects", payload);
        toast.success("Project published");
      }
      navigate("/admin/projects");
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("Project save failed:", err.response?.status, err.response?.data, err.message);
      const detail = err.response?.data?.detail;
      const fallback = err.response?.status
        ? `Save failed (${err.response.status}). ${err.message || ""}`
        : `Network error: ${err.message || "could not reach server"}`;
      toast.error(formatApiErrorDetail(detail) || fallback);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="admin-project-form">
      <Link to="/admin/projects" className="inline-flex items-center gap-2 text-sm text-slate-600 hover:text-slate-950 mb-4">
        <ArrowLeft className="w-4 h-4" /> Back to projects
      </Link>
      <h1 className="font-heading text-2xl sm:text-3xl font-medium text-slate-950 mb-6 sm:mb-8">{isEdit ? "Edit Project" : "New Project"}</h1>

      <form onSubmit={submit} className="grid lg:grid-cols-[1fr,360px] gap-6">
        <div className="space-y-4">
          {/* CORE FIELDS */}
          <div className="bg-white border border-slate-200 rounded-lg p-5 sm:p-6 space-y-4">
            <Field label="Location">
              <input
                required
                value={form.location}
                onChange={(e) => setForm({ ...form, location: e.target.value })}
                placeholder="Lagos, Nigeria"
                data-testid="form-location"
                className="w-full px-4 py-3 rounded-md border border-slate-200 text-sm"
              />
            </Field>
            <Field label="Description / Write-up">
              <textarea
                rows={5}
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                placeholder="Tell the story of this project — materials, scope, transformation…"
                data-testid="form-description"
                className="w-full px-4 py-3 rounded-md border border-slate-200 text-sm"
              />
            </Field>
          </div>

          {/* MEDIA */}
          <div className="bg-white border border-slate-200 rounded-lg p-5 sm:p-6">
            <h2 className="font-heading text-lg font-medium text-slate-950 mb-1">Media</h2>
            <p className="text-xs text-slate-500 mb-4">Add photos and videos. The first image becomes the cover automatically.</p>

            {/* PRIMARY OPTION: paste a YouTube/Vimeo link — instant, no upload, no processing */}
            <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3.5 mb-4">
              <div className="flex items-center gap-2 mb-2 text-emerald-900">
                <Youtube className="w-4 h-4" />
                <span className="text-xs font-semibold tracking-wide uppercase">Recommended — Paste a YouTube or Vimeo link</span>
              </div>
              <p className="text-[11px] text-emerald-800/80 mb-2.5 leading-relaxed">
                No upload needed. Plays instantly worldwide via YouTube/Vimeo's CDN.
                Most reliable way to add video.
              </p>
              <div className="flex gap-2">
                <input
                  type="url"
                  value={embedUrl}
                  onChange={(e) => setEmbedUrl(e.target.value)}
                  placeholder="https://youtube.com/watch?v=… or https://vimeo.com/…"
                  data-testid="form-embed-url-input"
                  onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addVideoEmbed(); } }}
                  className="flex-1 px-3 py-2 rounded-md border border-emerald-300 text-sm bg-white"
                />
                <button
                  type="button"
                  onClick={addVideoEmbed}
                  disabled={addingEmbed || !embedUrl.trim()}
                  data-testid="form-embed-add-btn"
                  className="inline-flex items-center gap-1.5 bg-emerald-600 hover:bg-emerald-700 text-white px-3.5 py-2 rounded-md text-sm font-medium disabled:opacity-50 whitespace-nowrap"
                >
                  <Link2 className="w-4 h-4" /> {addingEmbed ? "Adding…" : "Add"}
                </button>
              </div>
            </div>

            <input ref={fileRef} type="file" accept="image/*,video/*" multiple onChange={onAddMedia} data-testid="form-media-upload" className="hidden" />
            <input ref={replaceRef} type="file" accept="image/*,video/*" onChange={onReplaceMedia} data-testid="form-media-replace-input" className="hidden" />
            <button type="button" onClick={() => fileRef.current?.click()} disabled={uploading}
              className="inline-flex items-center gap-2 border border-slate-200 hover:bg-slate-50 px-4 py-2 rounded-md text-sm">
              <Upload className="w-4 h-4" /> {uploading ? "Uploading…" : "Upload photo or video file"}
            </button>

            <div className="grid grid-cols-3 sm:grid-cols-4 gap-3 mt-4">
              {form.media?.map((m, i) => {
                const isVideo = m.type === "video" || isVideoUrl(m.url);
                const isEmbed = !!m.embed_provider;
                return (
                  <div key={i} data-media-slot={i} data-testid={`media-slot-${i}`}
                    className="relative aspect-square overflow-hidden bg-slate-100 group rounded-md transition-shadow">
                    {isEmbed ? (
                      <img src={m.poster_url || ""} alt="" className="w-full h-full object-cover" />
                    ) : isVideo ? (
                      <video src={fileUrl(m.url)} muted playsInline preload="metadata" className="w-full h-full object-cover" />
                    ) : (
                      <img src={fileUrl(m.url)} alt="" className="w-full h-full object-cover" />
                    )}
                    {isVideo && (
                      <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                        <Play className="w-7 h-7 text-white drop-shadow-md fill-white" />
                      </div>
                    )}
                    {isEmbed && (
                      <div className="absolute bottom-1 left-1 px-1.5 py-0.5 rounded bg-black/70 text-white text-[9px] uppercase tracking-wider">
                        {m.embed_provider}
                      </div>
                    )}
                    <div className="absolute top-1 right-1 flex gap-1 opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity">
                      <button type="button" onClick={() => triggerReplace(i)} data-testid={`replace-media-${i}`} title="Replace"
                        className="p-1 bg-white/95 hover:bg-white rounded shadow-sm">
                        <ReplaceIcon className="w-3.5 h-3.5 text-slate-700" />
                      </button>
                      <button type="button" onClick={() => removeMedia(i)} data-testid={`remove-media-${i}`} title="Remove"
                        className="p-1 bg-white/95 hover:bg-white rounded shadow-sm">
                        <X className="w-3.5 h-3.5 text-slate-700" />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* SEO */}
          <div className="bg-white border border-slate-200 rounded-lg p-5 sm:p-6 space-y-4">
            <h2 className="font-heading text-lg font-medium text-slate-950">SEO</h2>
            <Field label="Slug (URL identifier)">
              <input
                value={form.slug || ""}
                onChange={(e) => setForm({ ...form, slug: slugify(e.target.value) })}
                placeholder="auto-generated from location if blank"
                data-testid="form-slug"
                className="w-full px-4 py-3 rounded-md border border-slate-200 text-sm font-mono"
              />
            </Field>
            <Field label="SEO Title">
              <input
                value={form.seo_title || ""}
                onChange={(e) => setForm({ ...form, seo_title: e.target.value })}
                placeholder="Auto-generated if blank"
                className="w-full px-4 py-3 rounded-md border border-slate-200 text-sm"
              />
            </Field>
            <Field label="SEO Description">
              <textarea
                rows={2}
                value={form.seo_description || ""}
                onChange={(e) => setForm({ ...form, seo_description: e.target.value })}
                placeholder="Auto-generated if blank"
                className="w-full px-4 py-3 rounded-md border border-slate-200 text-sm"
              />
            </Field>
          </div>
        </div>

        {/* SIDEBAR */}
        <aside className="space-y-4">
          <div className="bg-white border border-slate-200 rounded-lg p-5 sm:p-6 space-y-4 lg:sticky lg:top-6">
            <h2 className="font-heading text-lg font-medium text-slate-950">Status</h2>
            <label className="flex items-center justify-between text-sm">
              <span>Published</span>
              <input type="checkbox" checked={form.is_published}
                onChange={(e) => setForm({ ...form, is_published: e.target.checked, is_archived: e.target.checked ? false : form.is_archived })}
                data-testid="form-is-published" />
            </label>
            <label className="flex items-center justify-between text-sm">
              <span>Pinned (featured)</span>
              <input type="checkbox" checked={form.is_pinned}
                onChange={(e) => setForm({ ...form, is_pinned: e.target.checked })}
                data-testid="form-is-pinned" />
            </label>
            <label className="flex items-center justify-between text-sm">
              <span>Archived (hidden)</span>
              <input type="checkbox" checked={!!form.is_archived}
                onChange={(e) => setForm({ ...form, is_archived: e.target.checked, is_published: e.target.checked ? false : form.is_published })}
                data-testid="form-is-archived" />
            </label>

            {/* Cover */}
            <div>
              <div className="text-xs uppercase tracking-widest text-slate-500 mb-1.5">Cover thumbnail</div>
              <div className="aspect-square bg-slate-100 overflow-hidden rounded-md border border-slate-200 mb-2">
                {form.cover_image ? (
                  isVideoUrl(form.cover_image) ? (
                    <div className="w-full h-full flex flex-col items-center justify-center text-center px-3 text-[11px] text-slate-500">
                      <Play className="w-6 h-6 mb-1 opacity-50" />
                      <span className="leading-snug">Cover poster will be auto-generated from this video after processing.</span>
                    </div>
                  ) : (
                    <img src={fileUrl(form.cover_image)} alt="" className="w-full h-full object-cover" />
                  )
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-xs text-slate-400">Auto from media</div>
                )}
              </div>
              <input ref={coverRef} type="file" accept="image/*" onChange={onUploadCover} className="hidden" />
              <button type="button" onClick={() => coverRef.current?.click()}
                className="w-full inline-flex items-center justify-center gap-2 border border-slate-200 hover:bg-slate-50 px-3 py-2 rounded-md text-xs">
                <Upload className="w-3.5 h-3.5" /> {form.cover_image ? "Replace cover" : "Upload custom cover"}
              </button>
            </div>

            <button type="submit" disabled={submitting} data-testid="form-submit-btn"
              className="w-full inline-flex items-center justify-center gap-2 bg-slate-950 hover:bg-slate-800 text-white px-5 py-3 rounded-md text-sm font-medium disabled:opacity-50">
              <Save className="w-4 h-4" /> {submitting ? "Saving…" : isEdit ? "Update Project" : "Publish Project"}
            </button>
          </div>
        </aside>
      </form>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block text-sm">
      <div className="text-xs uppercase tracking-widest text-slate-500 mb-1.5">{label}</div>
      {children}
    </label>
  );
}
